import React, { useState, useEffect } from 'react';
import Textarea from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import ReviewerOptIn from '../../components/ReviewerOptIn';
import { profileService } from '../../services/profileService';
import countryOptions from '../../data/countries';

const UserProfileManagement = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { user, userProfile, updateProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
  // Preview of a *newly picked* file, distinct from avatarPreviewUrl which
  // holds the already-saved avatar. One blob URL per file, revoked on replace
  // and on unmount — building it inline in render minted a new URL on every
  // keystroke elsewhere in the form and never released any of them.
  const [avatarObjectUrl, setAvatarObjectUrl] = useState(null);
  useEffect(() => {
    if (!avatarFile) { setAvatarObjectUrl(null); return undefined; }
    const url = URL.createObjectURL(avatarFile);
    setAvatarObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [avatarFile]);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: '',
    institution: '',
    institutionType: '',
    department: '',
    academicRank: '',
    orcid: '',
    website: '',
    bio: '',
    subjectAreas: [],
    roles: [],
    experienceLevel: '',
    receiveUpdates: false,
    availableAsReviewer: true,
    reviewerKeywords: []
  });

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
      return;
    }

    if (userProfile) {
      setFormData({
        title: userProfile?.title || '',
        firstName: userProfile?.firstName || '',
        lastName: userProfile?.lastName || '',
        email: userProfile?.email || '',
        phone: userProfile?.phone || '',
        country: userProfile?.country || '',
        institution: userProfile?.institution || '',
        institutionType: userProfile?.institutionType || '',
        department: userProfile?.department || '',
        academicRank: userProfile?.academicRank || '',
        orcid: userProfile?.orcid || '',
        website: userProfile?.website || '',
        bio: userProfile?.bio || '',
        subjectAreas: userProfile?.subjectAreas || [],
        roles: userProfile?.roles || [],
        experienceLevel: userProfile?.experienceLevel || '',
        receiveUpdates: userProfile?.receiveUpdates || false,
        availableAsReviewer: userProfile?.availableAsReviewer ?? true,
        reviewerKeywords: userProfile?.reviewerKeywords || []
      });
    }
  }, [user, userProfile, navigate]);

  useEffect(() => {
    if (userProfile?.avatarUrl) {
      profileService?.getAvatarUrl(userProfile?.avatarUrl)?.then(url => {
        setAvatarPreviewUrl(url);
      })?.catch(() => setAvatarPreviewUrl(null));
    }
  }, [userProfile?.avatarUrl]);

  const handleAvatarFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file?.type)) {
        toastError(t('profileSettings.personalInformation.photoTypeError'));
        return;
      }
      if (file?.size > 2 * 1024 * 1024) {
        toastError(t('profileSettings.personalInformation.photoSizeError'));
        return;
      }
      setAvatarFile(file);
    }
  };

  const titleOptions = [
    { value: 'dr', label: t('profileSettings.titleOptions.dr') },
    { value: 'prof', label: t('profileSettings.titleOptions.prof') },
    { value: 'mr', label: t('profileSettings.titleOptions.mr') },
    { value: 'ms', label: t('profileSettings.titleOptions.ms') },
    { value: 'mrs', label: t('profileSettings.titleOptions.mrs') }
  ];

  const institutionTypeOptions = [
    { value: 'university', label: t('profileSettings.institutionTypes.university') },
    { value: 'research', label: t('profileSettings.institutionTypes.research') },
    { value: 'corporate', label: t('profileSettings.institutionTypes.corporate') },
    { value: 'government', label: t('profileSettings.institutionTypes.government') },
    { value: 'independent', label: t('profileSettings.institutionTypes.independent') },
    { value: 'other', label: t('profileSettings.institutionTypes.other') }
  ];

  const departmentOptions = [
    { value: 'cs', label: t('profileSettings.departments.cs') },
    { value: 'ai', label: t('profileSettings.departments.ai') },
    { value: 'medicine', label: t('profileSettings.departments.medicine') },
    { value: 'biology', label: t('profileSettings.departments.biology') },
    { value: 'physics', label: t('profileSettings.departments.physics') },
    { value: 'chemistry', label: t('profileSettings.departments.chemistry') },
    { value: 'engineering', label: t('profileSettings.departments.engineering') },
    { value: 'business', label: t('profileSettings.departments.business') },
    { value: 'economics', label: t('profileSettings.departments.economics') },
    { value: 'other', label: t('profileSettings.departments.other') }
  ];

  const academicRankOptions = [
    { value: 'student', label: t('profileSettings.academicRanks.student') },
    { value: 'postdoc', label: t('profileSettings.academicRanks.postdoc') },
    { value: 'assistant', label: t('profileSettings.academicRanks.assistant') },
    { value: 'associate', label: t('profileSettings.academicRanks.associate') },
    { value: 'full', label: t('profileSettings.academicRanks.full') },
    { value: 'researcher', label: t('profileSettings.academicRanks.researcher') },
    { value: 'engineer', label: t('profileSettings.academicRanks.engineer') },
    { value: 'other', label: t('profileSettings.academicRanks.other') }
  ];

  const experienceLevelOptions = [
    { value: 'beginner', label: t('profileSettings.experienceLevels.beginner') },
    { value: 'intermediate', label: t('profileSettings.experienceLevels.intermediate') },
    { value: 'advanced', label: t('profileSettings.experienceLevels.advanced') },
    { value: 'expert', label: t('profileSettings.experienceLevels.expert') }
  ];

  const subjectAreas = [
    { id: 'ai', label: t('profileSettings.expertiseRoles.subjects.ai'), icon: 'Brain' },
    { id: 'ml', label: t('profileSettings.expertiseRoles.subjects.ml'), icon: 'Cpu' },
    { id: 'nlp', label: t('profileSettings.expertiseRoles.subjects.nlp'), icon: 'MessageSquare' },
    { id: 'cv', label: t('profileSettings.expertiseRoles.subjects.cv'), icon: 'Eye' },
    { id: 'medicine', label: t('profileSettings.expertiseRoles.subjects.medicine'), icon: 'Heart' },
    { id: 'biology', label: t('profileSettings.expertiseRoles.subjects.biology'), icon: 'Microscope' },
    { id: 'business', label: t('profileSettings.expertiseRoles.subjects.business'), icon: 'Briefcase' },
    { id: 'economics', label: t('profileSettings.expertiseRoles.subjects.economics'), icon: 'TrendingUp' },
    { id: 'engineering', label: t('profileSettings.expertiseRoles.subjects.engineering'), icon: 'Wrench' },
    { id: 'physics', label: t('profileSettings.expertiseRoles.subjects.physics'), icon: 'Atom' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const handleSubjectToggle = (subjectId) => {
    const currentSubjects = formData?.subjectAreas || [];
    const newSubjects = currentSubjects?.includes(subjectId)
      ? currentSubjects?.filter(id => id !== subjectId)
      : [...currentSubjects, subjectId];
    handleChange('subjectAreas', newSubjects);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    try {
      let updatedData = { ...formData };

      if (avatarFile) {
        if (userProfile?.avatarUrl) {
          await profileService?.deleteAvatar(userProfile?.avatarUrl);
        }
        const avatarPath = await profileService?.uploadAvatar(avatarFile, user?.id);
        updatedData.avatarUrl = avatarPath;
      }

      const { error: updateError } = await updateProfile(updatedData);

      if (updateError) {
        throw updateError;
      }

      setAvatarFile(null);
      setSuccess(t('profileSettings.successMessage'));
      setIsEditing(false);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || t('profileSettings.errorMessage'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (userProfile) {
      setFormData({
        title: userProfile?.title || '',
        firstName: userProfile?.firstName || '',
        lastName: userProfile?.lastName || '',
        email: userProfile?.email || '',
        phone: userProfile?.phone || '',
        country: userProfile?.country || '',
        institution: userProfile?.institution || '',
        institutionType: userProfile?.institutionType || '',
        department: userProfile?.department || '',
        academicRank: userProfile?.academicRank || '',
        orcid: userProfile?.orcid || '',
        website: userProfile?.website || '',
        bio: userProfile?.bio || '',
        subjectAreas: userProfile?.subjectAreas || [],
        roles: userProfile?.roles || [],
        experienceLevel: userProfile?.experienceLevel || '',
        receiveUpdates: userProfile?.receiveUpdates || false
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const getDisplayValue = (value, options) => {
    const option = options?.find(opt => opt?.value === value);
    return option?.label || value || t('profileSettings.notSpecified');
  };

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="page-shell py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-3xl font-semibold text-foreground">{t('profileSettings.title')}</h1>
              {!isEditing ? (
                <Button
                  variant="default"
                  iconName="Edit"
                  iconPosition="left"
                  onClick={() => setIsEditing(true)}
                >
                  {t('profileSettings.editProfile')}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    {t('profileSettings.cancel')}
                  </Button>
                  <Button
                    variant="default"
                    iconName="Save"
                    iconPosition="left"
                    onClick={handleSave}
                    loading={isSaving}
                  >
                    {t('profileSettings.saveChanges')}
                  </Button>
                </div>
              )}
            </div>
            <p className="text-muted-foreground">{t('profileSettings.subtitle')}</p>
          </div>

          {/* Success/Error Messages */}
          {success && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-3">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-destructive" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Profile Content - Read-only or Edit mode */}
          {!isEditing ? (
            // READ-ONLY VIEW
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.personalInformation.title')}</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="w-20 h-20 overflow-hidden border-2 border-primary/20 bg-muted flex items-center justify-center flex-shrink-0">
                      {avatarPreviewUrl ? (
                        <img src={avatarPreviewUrl} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <Icon name="User" size={32} className="text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-foreground">
                        {formData?.title && getDisplayValue(formData?.title, titleOptions) + ' '}
                        {formData?.firstName} {formData?.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">{formData?.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.personalInformation.labels.name')}</label>
                      <p className="text-base text-foreground">
                        {formData?.title && getDisplayValue(formData?.title, titleOptions) + ' '}
                        {formData?.firstName} {formData?.lastName}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.personalInformation.labels.email')}</label>
                      <p className="text-base text-foreground">{formData?.email || t('profileSettings.notSpecified')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.personalInformation.labels.phone')}</label>
                      <p className="text-base text-foreground">{formData?.phone || t('profileSettings.notSpecified')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.personalInformation.labels.country')}</label>
                      <p className="text-base text-foreground">{getDisplayValue(formData?.country, countryOptions)}</p>
                    </div>
                  </div>
                  {formData?.bio && (
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.personalInformation.labels.bio')}</label>
                      <p className="text-base text-foreground">{formData?.bio}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Institutional Information */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.institutionalInformation.title')}</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.institutionalInformation.labels.institution')}</label>
                      <p className="text-base text-foreground">{formData?.institution || t('profileSettings.notSpecified')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.institutionalInformation.labels.institutionType')}</label>
                      <p className="text-base text-foreground">{getDisplayValue(formData?.institutionType, institutionTypeOptions)}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.institutionalInformation.labels.department')}</label>
                      <p className="text-base text-foreground">{getDisplayValue(formData?.department, departmentOptions)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.institutionalInformation.labels.academicRank')}</label>
                      <p className="text-base text-foreground">{getDisplayValue(formData?.academicRank, academicRankOptions)}</p>
                    </div>
                  </div>
                  {formData?.orcid && (
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.institutionalInformation.labels.orcid')}</label>
                      <p className="text-base text-foreground">{formData?.orcid}</p>
                    </div>
                  )}
                  {formData?.website && (
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.institutionalInformation.labels.website')}</label>
                      <a href={formData?.website} target="_blank" rel="noopener noreferrer" className="text-base text-primary hover:underline">
                        {formData?.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Expertise & Roles */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.expertiseRoles.title')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-muted-foreground mb-2">{t('profileSettings.expertiseRoles.subjectAreas')}</label>
                    <div className="flex flex-wrap gap-2">
                      {formData?.subjectAreas?.length > 0 ? (
                        formData?.subjectAreas?.map((areaId) => {
                          const area = subjectAreas?.find(s => s?.id === areaId);
                          return area ? (
                            <span key={areaId} className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary text-sm">
                              <Icon name={area?.icon} size={16} />
                              {area?.label}
                            </span>
                          ) : null;
                        })
                      ) : (
                        <p className="text-base text-foreground">{t('profileSettings.expertiseRoles.noSubjectAreas')}</p>
                      )}
                    </div>
                  </div>
                  {/* Self-declared roles removed. Being a chair, reviewer or
                      author is granted per conference (conference_roles), not
                      claimed on a profile — so showing it here was misleading:
                      ticking "Chair" conferred nothing. Willingness to review
                      lives in "Available as reviewer" + keywords above, which
                      is what chairs actually search when assigning. */}
                  {formData?.experienceLevel && (
                    <div>
                      <label className="block text-sm font-medium text-muted-foreground mb-1">{t('profileSettings.expertiseRoles.experienceLevel')}</label>
                      <p className="text-base text-foreground">{getDisplayValue(formData?.experienceLevel, experienceLevelOptions)}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.notificationPreferences.title')}</h2>
                <div className="flex items-center gap-3">
                  <Icon name={formData?.receiveUpdates ? "CheckCircle" : "Circle"} size={20} className={formData?.receiveUpdates ? "text-primary" : "text-muted-foreground"} />
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('profileSettings.notificationPreferences.receiveUpdates')}</p>
                    <p className="text-xs text-muted-foreground">{t('profileSettings.notificationPreferences.receiveUpdatesDescription')}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // EDIT MODE
            <div className="space-y-6">
              {/* Personal Information */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.personalInformation.title')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t('profileSettings.personalInformation.profilePhoto')}</label>
                    <div className="flex items-center gap-4">
                      <div className="relative w-20 h-20 overflow-hidden border-2 border-primary/20 bg-muted flex items-center justify-center flex-shrink-0">
                        {avatarFile ? (
                          <img src={avatarObjectUrl} alt="New profile" className="w-full h-full object-cover" />
                        ) : avatarPreviewUrl ? (
                          <img src={avatarPreviewUrl} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <Icon name="User" size={32} className="text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => document.getElementById('avatar-file-input')?.click()}
                          iconName="Camera"
                          iconPosition="left"
                        >
                          {avatarPreviewUrl || avatarFile ? t('profileSettings.personalInformation.changePhoto') : t('profileSettings.personalInformation.uploadPhoto')}
                        </Button>
                        {avatarFile && (
                          <p className="text-xs text-muted-foreground">{avatarFile?.name}</p>
                        )}
                        <p className="text-xs text-muted-foreground">{t('profileSettings.personalInformation.photoHelp')}</p>
                      </div>
                      <input
                        id="avatar-file-input"
                        // Hidden input driven by the visible button above; it still
                        // needs its own accessible name.
                        aria-label={t('profileSettings.personalInformation.uploadPhoto', { defaultValue: 'Upload profile photo' })}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleAvatarFileSelect}
                        className="hidden"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select
                      label={t('profileSettings.personalInformation.labels.title')}
                      options={titleOptions}
                      value={formData?.title}
                      onChange={(value) => handleChange('title', value)}
                      placeholder={t('profileSettings.personalInformation.placeholders.selectTitle')}
                    />
                    <div className="md:col-span-2">
                      <Input
                        label={t('profileSettings.personalInformation.labels.firstName')}
                        type="text"
                        value={formData?.firstName}
                        onChange={(e) => handleChange('firstName', e?.target?.value)}
                        required
                      />
                    </div>
                  </div>
                  <Input
                    label={t('profileSettings.personalInformation.labels.lastName')}
                    type="text"
                    value={formData?.lastName}
                    onChange={(e) => handleChange('lastName', e?.target?.value)}
                    required
                  />
                  <Input
                    label={t('profileSettings.personalInformation.labels.emailAddress')}
                    type="email"
                    value={formData?.email}
                    disabled
                    description={t('profileSettings.personalInformation.labels.emailCannotChange')}
                  />
                  <Input
                    label={t('profileSettings.personalInformation.labels.phoneNumber')}
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleChange('phone', e?.target?.value)}
                  />
                  <Select
                    label={t('profileSettings.personalInformation.labels.country')}
                    options={countryOptions}
                    value={formData?.country}
                    onChange={(value) => handleChange('country', value)}
                    placeholder={t('profileSettings.personalInformation.placeholders.selectCountry')}
                    searchable
                    required
                  />
                  <div>
                    <Textarea
                      id="profile-bio"
                      value={formData?.bio || ''}
                      onChange={(e) => handleChange('bio', e?.target?.value)}
                      rows={4}
                      maxLength={600}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      placeholder={t('profileSettings.personalInformation.placeholders.bio')}
                    />
                  </div>
                </div>
              </div>

              {/* Institutional Information */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.institutionalInformation.title')}</h2>
                <div className="space-y-4">
                  <Input
                    label={t('profileSettings.institutionalInformation.labels.institutionName')}
                    type="text"
                    value={formData?.institution}
                    onChange={(e) => handleChange('institution', e?.target?.value)}
                    required
                  />
                  <Select
                    label={t('profileSettings.institutionalInformation.labels.institutionType')}
                    options={institutionTypeOptions}
                    value={formData?.institutionType}
                    onChange={(value) => handleChange('institutionType', value)}
                    placeholder={t('profileSettings.institutionalInformation.placeholders.selectInstitutionType')}
                    required
                  />
                  <Select
                    label={t('profileSettings.institutionalInformation.labels.departmentField')}
                    options={departmentOptions}
                    value={formData?.department}
                    onChange={(value) => handleChange('department', value)}
                    placeholder={t('profileSettings.institutionalInformation.placeholders.selectDepartment')}
                    searchable
                    required
                  />
                  <Select
                    label={t('profileSettings.institutionalInformation.labels.academicRankPosition')}
                    options={academicRankOptions}
                    value={formData?.academicRank}
                    onChange={(value) => handleChange('academicRank', value)}
                    placeholder={t('profileSettings.institutionalInformation.placeholders.selectPosition')}
                    required
                  />
                  <Input
                    label={t('profileSettings.institutionalInformation.labels.orcid')}
                    type="text"
                    value={formData?.orcid}
                    onChange={(e) => handleChange('orcid', e?.target?.value)}
                    placeholder={t('profileSettings.institutionalInformation.placeholders.orcid')}
                  />
                  <Input
                    label={t('profileSettings.institutionalInformation.labels.websiteProfile')}
                    type="url"
                    value={formData?.website}
                    onChange={(e) => handleChange('website', e?.target?.value)}
                    placeholder={t('profileSettings.institutionalInformation.placeholders.website')}
                  />
                </div>
              </div>

              {/* Expertise & Roles */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.expertiseRoles.title')}</h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">{t('profileSettings.expertiseRoles.subjectAreas')}</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {subjectAreas?.map((subject) => (
                        <button
                          key={subject?.id}
                          type="button"
                          onClick={() => handleSubjectToggle(subject?.id)}
                          className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-250 cursor-pointer ${
                            (formData?.subjectAreas || [])?.includes(subject?.id)
                              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted'
                          }`}
                        >
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-250 ${
                              (formData?.subjectAreas || [])?.includes(subject?.id)
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            <Icon name={subject?.icon} size={20} />
                          </div>
                          <span className="text-sm font-medium text-foreground">{subject?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      {t('profileSettings.reviewerDirectory', { defaultValue: 'Reviewer directory' })}
                    </label>
                    <ReviewerOptIn
                      available={formData?.availableAsReviewer}
                      keywords={formData?.reviewerKeywords}
                      onAvailableChange={(v) => handleChange('availableAsReviewer', v)}
                      onKeywordsChange={(kw) => handleChange('reviewerKeywords', kw)}
                    />
                  </div>

                  {/* Role picker removed — see note in the read-only view above.
                      Roles are per-conference and granted by an organiser; they
                      were never read for any permission decision here. Existing
                      stored values are passed through untouched on save so no
                      data is lost while the migration rolls out. */}

                  <Select
                    label={t('profileSettings.expertiseRoles.experienceLevel')}
                    options={experienceLevelOptions}
                    value={formData?.experienceLevel}
                    onChange={(value) => handleChange('experienceLevel', value)}
                    placeholder={t('profileSettings.expertiseRoles.selectExperienceLevel')}
                    required
                  />
                </div>
              </div>

              {/* Notification Preferences */}
              <div className="bg-card border border-border p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">{t('profileSettings.notificationPreferences.title')}</h2>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.receiveUpdates}
                    onChange={(e) => handleChange('receiveUpdates', e?.target?.checked)}
                    className="h-4 w-4 border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground">{t('profileSettings.notificationPreferences.receiveUpdates')}</p>
                    <p className="text-xs text-muted-foreground">{t('profileSettings.notificationPreferences.receiveUpdatesDescription')}</p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default UserProfileManagement;
